/**
 * DEMO-ONLY: Discovery prototype state (roles, affiliates, address visibility,
 * purchase rules, account catalogs/pricing).
 * Stakeholder-approved discovery UX — DEMO-ONLY mock; ICM contracts not confirmed.
 */
import {
  PRESIDIO_DISCOVERY_AFFILIATES,
  type PresidioDiscoveryAccessRequest,
  type PresidioDiscoveryAddressMeta,
  type PresidioDiscoveryAffiliate,
  type PresidioDiscoveryCatalogConfig,
  type PresidioDiscoveryPrimaryRole,
  type PresidioDiscoveryPendingApprovalOrder,
  type PresidioDiscoveryPendingApprovalStatus,
  type PresidioDiscoveryPurchaseRule,
  type PresidioDiscoveryPurchaseRuleType,
  type PresidioDiscoveryState,
  type PresidioDiscoveryUser,
} from '../../presidio-discovery/models/presidio-discovery.model';
import { PRESIDIO_MOCK_PRODUCT_NAMES } from './presidio-mock-product-names';
import { PRESIDIO_MOCK_PRODUCT_PRICES } from './presidio-mock-product-prices';
import { countPresidioQuotesPendingApproval } from './presidio-mock-quotes';
import { getPresidioMockActiveCustomerNo, getPresidioMockActiveLogin, isPresidioMockCsrLogin } from './presidio-mock-session';

export const PRESIDIO_DISCOVERY_STATE_KEY = 'presidio-mock-discovery-state';

/** Re-export contracts for existing demo imports. */
export {
  PRESIDIO_DISCOVERY_AFFILIATES,
  type PresidioDiscoveryAccessRequest,
  type PresidioDiscoveryAddressMeta,
  type PresidioDiscoveryAffiliate,
  type PresidioDiscoveryCatalogConfig,
  type PresidioDiscoveryPendingApprovalOrder,
  type PresidioDiscoveryPendingApprovalStatus,
  type PresidioDiscoveryPrimaryRole,
  type PresidioDiscoveryPurchaseRule,
  type PresidioDiscoveryPurchaseRuleType,
  type PresidioDiscoveryState,
  type PresidioDiscoveryUser,
};

/** Full mock catalog SKUs (product JSON + priced SKUs). Acme has broad access. */
const ALL_MOCK_SKUS = Array.from(
  new Set([...Object.keys(PRESIDIO_MOCK_PRODUCT_NAMES), ...Object.keys(PRESIDIO_MOCK_PRODUCT_PRICES)])
).sort();

function skusMatching(...patterns: RegExp[]): string[] {
  return ALL_MOCK_SKUS.filter(sku => patterns.some(p => p.test(sku)));
}

export function discoveryProductName(sku: string): string {
  return PRESIDIO_MOCK_PRODUCT_NAMES[sku] || sku;
}

/** Products the active account catalog allows (same filter as storefront). */
export function getAccessibleProductsForActiveCustomer(): { sku: string; name: string }[] {
  const catalog = getCatalogForActiveCustomer();
  const skus = catalog?.allowedSkus?.length ? catalog.allowedSkus : ALL_MOCK_SKUS;
  return skus.map(sku => ({ sku, name: discoveryProductName(sku) }));
}

/**
 * Search account-accessible products by SKU or product name.
 * Empty query returns the full account catalog (caller may cap display).
 */
export function searchDiscoverySkus(query: string, exclude: string[] = []): { sku: string; name: string }[] {
  const q = (query || '').trim().toLowerCase();
  const excluded = new Set(exclude.map(s => s.toUpperCase()));
  const accessible = getAccessibleProductsForActiveCustomer().filter(
    item => !excluded.has(item.sku.toUpperCase())
  );
  if (!q) {
    return accessible;
  }
  const words = q.split(/\s+/).filter(Boolean);
  return accessible.filter(item => {
    const skuLower = item.sku.toLowerCase();
    const nameLower = item.name.toLowerCase();
    const haystack = `${skuLower} ${nameLower} ${skuLower.replace(/^prs-/, '').replace(/-/g, ' ')}`;
    return words.every(word => haystack.includes(word));
  });
}

/** @deprecated Use getAccessibleProductsForActiveCustomer / searchDiscoverySkus. */
export const PRESIDIO_DISCOVERY_SKU_SEARCH_CATALOG: readonly { sku: string; name: string }[] =
  ALL_MOCK_SKUS.map(sku => ({ sku, name: discoveryProductName(sku) }));

/** Seed catalogs — Acme broad; Beta networking-heavy; Gamma education / endpoint subset. */
const BETA_ALLOWED_SKUS = skusMatching(
  /PRS-(CISCO|PAN|FTNT|ARISTA|JUN|HPE|SW|EDGE|SEC|ZT|UMBRELLA|N9K|DELL-R)/
);
const GAMMA_ALLOWED_SKUS = skusMatching(
  /PRS-(LEN|LAT|HP-|OPTI|MON|UCC|TEL|IPHONE|GAL|PIX|WEBCAM|KB|MOUSE|DOCK|BAG|MDM|PRT|SCAN|PAPER|TONER|CISCO-ISE|CISCO-C9300)/
);

const SEED_CATALOGS: Record<string, PresidioDiscoveryCatalogConfig> = {
  'PRESIDIO-MOCK': {
    customerNo: 'PRESIDIO-MOCK',
    allowedSkus: ALL_MOCK_SKUS,
    negotiatedPrices: {
      'CISCO-C9300': 7200,
      'ISE-3695': 6800,
      'NV-DGX-H100': 198000,
      'PA-3220': 2100,
      'PRS-DELL-R760': 9800,
      'T14-G5': 1499,
      'LAT-7440': 1899,
    },
    pricingNotes: [
      'Fixed SKU price: T14-G5 @ $1,499',
      'SKU discount: CISCO-C9300 negotiated $7,200',
      'Manufacturer discount: Cisco Catalyst family',
      'Catalog/contract discount: Acme Healthcare EA',
    ],
  },
  'PRESIDIO-AFF-BETA': {
    customerNo: 'PRESIDIO-AFF-BETA',
    allowedSkus: BETA_ALLOWED_SKUS.length ? BETA_ALLOWED_SKUS : ['CISCO-C9300', 'PA-3220', 'PRS-HPE-DL380'],
    negotiatedPrices: {
      'CISCO-C9300': 6900,
      'PA-3220': 1950,
      'PRS-DELL-R760': 9200,
      'ISE-3695': 6400,
      'PRS-HPE-DL380': 8700,
    },
    pricingNotes: [
      'Fixed SKU price: PA-3220 @ $1,950',
      'SKU discount: CISCO-C9300 @ $6,900 (Beta)',
      'Manufacturer discount: Palo Alto',
      'Catalog/contract discount: Beta Manufacturing net',
    ],
  },
  'PRESIDIO-AFF-GAMMA': {
    customerNo: 'PRESIDIO-AFF-GAMMA',
    allowedSkus: GAMMA_ALLOWED_SKUS.length
      ? GAMMA_ALLOWED_SKUS
      : ['T14-G5', 'LAT-5540', 'ISE-3695', 'CISCO-C9300'],
    negotiatedPrices: {
      'T14-G5': 1099,
      'LAT-5540': 1299,
      'ISE-3695': 7200,
      'CISCO-C9300': 7500,
    },
    pricingNotes: [
      'Fixed SKU price: T14-G5 @ $1,099 (education)',
      'SKU discount: Latitude education rate',
      'Manufacturer discount: Lenovo education',
      'Catalog/contract discount: Gamma Education',
    ],
  },
};

/** Align with MOCK_ADDRESS_BY_ID keys in mock.interceptor.presidio.ts */
const DISCOVERY_SHIP_ADDRESS_IDS = [
  'addr-ship',
  'addr-ship-dallas',
  'addr-ship-chicago',
  'addr-ship-nyc',
  'addr-ship-seattle',
  'addr-ship-atlanta',
  'addr-ship-denver',
  'addr-ship-phoenix',
  'addr-ship-miami',
] as const;

const DISCOVERY_BILL_ADDRESS_IDS = ['addr-inv'] as const;

/** Full address lines aligned with MOCK_ADDRESS_BY_ID / customer address fixtures. */
const DISCOVERY_ADDRESS_DETAILS: Record<
  string,
  {
    label: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    mainDivisionCode: string;
    postalCode: string;
    country: string;
  }
> = {
  'addr-inv': {
    label: 'HQ — Billing',
    addressLine1: '100 Healthcare Way',
    addressLine2: 'Suite 400',
    city: 'Austin',
    mainDivisionCode: 'TX',
    postalCode: '78701',
    country: 'United States',
  },
  'addr-ship': {
    label: 'Data Center — Shipping',
    addressLine1: '500 Server Farm Blvd',
    city: 'Austin',
    mainDivisionCode: 'TX',
    postalCode: '78758',
    country: 'United States',
  },
  'addr-ship-dallas': {
    label: 'Dallas Distribution',
    addressLine1: '2200 Commerce St',
    city: 'Dallas',
    mainDivisionCode: 'TX',
    postalCode: '75201',
    country: 'United States',
  },
  'addr-ship-chicago': {
    label: 'Midwest Hub',
    addressLine1: '233 S Wacker Dr',
    addressLine2: 'Floor 12',
    city: 'Chicago',
    mainDivisionCode: 'IL',
    postalCode: '60606',
    country: 'United States',
  },
  'addr-ship-nyc': {
    label: 'Northeast Office',
    addressLine1: '1 Penn Plaza',
    city: 'New York',
    mainDivisionCode: 'NY',
    postalCode: '10119',
    country: 'United States',
  },
  'addr-ship-seattle': {
    label: 'Pacific NW',
    addressLine1: '1201 3rd Ave',
    city: 'Seattle',
    mainDivisionCode: 'WA',
    postalCode: '98101',
    country: 'United States',
  },
  'addr-ship-atlanta': {
    label: 'Southeast Depot',
    addressLine1: '191 Peachtree St NE',
    city: 'Atlanta',
    mainDivisionCode: 'GA',
    postalCode: '30303',
    country: 'United States',
  },
  'addr-ship-denver': {
    label: 'Mountain Region',
    addressLine1: '1801 California St',
    city: 'Denver',
    mainDivisionCode: 'CO',
    postalCode: '80202',
    country: 'United States',
  },
  'addr-ship-phoenix': {
    label: 'Banner Health Campus',
    addressLine1: '1111 E McDowell Rd',
    city: 'Phoenix',
    mainDivisionCode: 'AZ',
    postalCode: '85006',
    country: 'United States',
  },
  'addr-ship-miami': {
    label: 'LatAm Staging',
    addressLine1: '200 S Biscayne Blvd',
    city: 'Miami',
    mainDivisionCode: 'FL',
    postalCode: '33131',
    country: 'United States',
  },
  'addr-inv-2': {
    label: 'Billing location 2',
    addressLine1: '2100 Research Blvd',
    city: 'Austin',
    mainDivisionCode: 'TX',
    postalCode: '78759',
    country: 'United States',
  },
  'addr-inv-3': {
    label: 'Billing location 3',
    addressLine1: '400 Congress Ave',
    addressLine2: 'Floor 8',
    city: 'Austin',
    mainDivisionCode: 'TX',
    postalCode: '78701',
    country: 'United States',
  },
  'addr-inv-4': {
    label: 'Billing location 4',
    addressLine1: '901 Main St',
    city: 'Dallas',
    mainDivisionCode: 'TX',
    postalCode: '75202',
    country: 'United States',
  },
  'addr-inv-5': {
    label: 'Billing location 5',
    addressLine1: '1000 Louisiana St',
    city: 'Houston',
    mainDivisionCode: 'TX',
    postalCode: '77002',
    country: 'United States',
  },
  'addr-inv-6': {
    label: 'Billing location 6',
    addressLine1: '600 W 7th St',
    city: 'Fort Worth',
    mainDivisionCode: 'TX',
    postalCode: '76102',
    country: 'United States',
  },
  'addr-inv-7': {
    label: 'Billing location 7',
    addressLine1: '300 Convent St',
    city: 'San Antonio',
    mainDivisionCode: 'TX',
    postalCode: '78205',
    country: 'United States',
  },
  'addr-inv-8': {
    label: 'Billing location 8',
    addressLine1: '201 E Anderson Ln',
    city: 'Austin',
    mainDivisionCode: 'TX',
    postalCode: '78752',
    country: 'United States',
  },
};

function addressDetailsFor(baseId: string): (typeof DISCOVERY_ADDRESS_DETAILS)[string] {
  return (
    DISCOVERY_ADDRESS_DETAILS[baseId] || {
      label: baseId,
      addressLine1: 'Address on file',
      city: '—',
      mainDivisionCode: '',
      postalCode: '',
      country: 'United States',
    }
  );
}

/** Base address id from meta id (`PRESIDIO-MOCK:addr-ship` → `addr-ship`). */
function baseAddressId(metaId: string): string {
  const parts = metaId.split(':');
  return parts.length > 1 ? parts.slice(1).join(':') : metaId;
}

function enrichAddressMeta(meta: PresidioDiscoveryAddressMeta): PresidioDiscoveryAddressMeta {
  const details = addressDetailsFor(baseAddressId(meta.id));
  return {
    ...meta,
    label: meta.label || details.label,
    addressLine1: meta.addressLine1 || details.addressLine1,
    addressLine2: meta.addressLine2 || details.addressLine2,
    city: meta.city || details.city,
    mainDivisionCode: meta.mainDivisionCode || details.mainDivisionCode,
    postalCode: meta.postalCode || details.postalCode,
    country: meta.country || details.country,
  };
}

function seedAddressMeta(): PresidioDiscoveryAddressMeta[] {
  const meta: PresidioDiscoveryAddressMeta[] = [];
  for (const customerNo of ['PRESIDIO-MOCK', 'PRESIDIO-AFF-BETA', 'PRESIDIO-AFF-GAMMA']) {
    DISCOVERY_SHIP_ADDRESS_IDS.forEach((id, i) => {
      const details = addressDetailsFor(id);
      meta.push({
        id: `${customerNo}:${id}`,
        customerNo,
        kind: 'shipping',
        /** Demo: Miami staging hidden on Acme only — restore via Show Hidden. */
        hidden: customerNo === 'PRESIDIO-MOCK' && id === 'addr-ship-miami',
        label: details.label,
        addressLine1: details.addressLine1,
        addressLine2: details.addressLine2,
        city: details.city,
        mainDivisionCode: details.mainDivisionCode,
        postalCode: details.postalCode,
        country: details.country,
        recentlyUsed: i < 3,
      });
    });
    DISCOVERY_BILL_ADDRESS_IDS.forEach((id, i) => {
      const details = addressDetailsFor(id);
      meta.push({
        id: `${customerNo}:${id}`,
        customerNo,
        kind: 'billing',
        hidden: false,
        label: details.label,
        addressLine1: details.addressLine1,
        addressLine2: details.addressLine2,
        city: details.city,
        mainDivisionCode: details.mainDivisionCode,
        postalCode: details.postalCode,
        country: details.country,
        recentlyUsed: i < 1,
      });
    });
    // Extra synthetic bill-tos for long-list / search demo (session-only visibility meta).
    for (let n = 2; n <= 8; n++) {
      const id = `addr-inv-${n}`;
      const details = addressDetailsFor(id);
      meta.push({
        id: `${customerNo}:${id}`,
        customerNo,
        kind: 'billing',
        hidden: customerNo === 'PRESIDIO-MOCK' && n === 8,
        label: details.label,
        addressLine1: details.addressLine1,
        addressLine2: details.addressLine2,
        city: details.city,
        mainDivisionCode: details.mainDivisionCode,
        postalCode: details.postalCode,
        country: details.country,
        recentlyUsed: n <= 3,
      });
    }
  }
  return meta;
}

function seedState(): PresidioDiscoveryState {
  return {
    users: [
      {
        login: 'jordan.hale@acme.com',
        firstName: 'Jordan',
        lastName: 'Hale',
        email: 'jordan.hale@acme.com',
        primaryRole: 'administrator',
        canApprove: true,
        status: 'Active',
        affiliateCustomerNos: ['PRESIDIO-MOCK', 'PRESIDIO-AFF-BETA', 'PRESIDIO-AFF-GAMMA'],
        approverLogin: undefined,
      },
      {
        login: 'alex.rivera@acme.com',
        firstName: 'Alex',
        lastName: 'Rivera',
        email: 'alex.rivera@acme.com',
        primaryRole: 'standard',
        canApprove: false,
        status: 'Active',
        affiliateCustomerNos: ['PRESIDIO-MOCK', 'PRESIDIO-AFF-BETA'],
        approverLogin: 'morgan.chen@acme.com',
      },
      {
        login: 'morgan.chen@acme.com',
        firstName: 'Morgan',
        lastName: 'Chen',
        email: 'morgan.chen@acme.com',
        primaryRole: 'standard',
        canApprove: true,
        status: 'Active',
        /** DEMO: single assigned account (Acme only) — no multi-account switcher. */
        affiliateCustomerNos: ['PRESIDIO-MOCK'],
      },
      {
        login: 'sam.patel@acme.com',
        firstName: 'Sam',
        lastName: 'Patel',
        email: 'sam.patel@acme.com',
        primaryRole: 'administrator',
        canApprove: false,
        status: 'Active',
        /**
         * Second Active administrator across the same affiliates as Jordan so either
         * can be demoted/deactivated without stranding an account (FR18.1.9).
         * Selective affiliate grants are still demonstrated on invite/create (Casey).
         */
        affiliateCustomerNos: ['PRESIDIO-MOCK', 'PRESIDIO-AFF-BETA', 'PRESIDIO-AFF-GAMMA'],
      },
      {
        login: 'casey.ng@acme.com',
        firstName: 'Casey',
        lastName: 'Ng',
        email: 'casey.ng@acme.com',
        primaryRole: 'standard',
        canApprove: false,
        status: 'Pending',
        affiliateCustomerNos: ['PRESIDIO-MOCK'],
        approverLogin: 'morgan.chen@acme.com',
      },
      {
        login: 'jamie.stone@acme.com',
        firstName: 'Jamie',
        lastName: 'Stone',
        email: 'jamie.stone@acme.com',
        primaryRole: 'standard',
        canApprove: false,
        status: 'Active',
        /** DEMO: single assigned account — shops Acme only; no Select account / switcher. */
        affiliateCustomerNos: ['PRESIDIO-MOCK'],
        // No purchase rules → no assigned approver (discovery UI clears approver when rules are empty).
        approverLogin: undefined,
      },
    ],
    purchaseRules: [
      {
        id: 'PR-1',
        userLogin: 'alex.rivera@acme.com',
        type: 'OrderSubtotal',
        threshold: 25000,
      },
      {
        id: 'PR-2',
        userLogin: 'alex.rivera@acme.com',
        type: 'LinePrice',
        threshold: 15000,
      },
      {
        id: 'PR-3',
        userLogin: 'alex.rivera@acme.com',
        type: 'Sku',
        sku: 'NV-DGX-H100',
      },
      {
        id: 'PR-4',
        userLogin: 'alex.rivera@acme.com',
        type: 'Sku',
        sku: 'CISCO-C9300',
      },
    ],
    addressMeta: seedAddressMeta(),
    accessRequests: [
      {
        id: 'AR-SEED-CASEY',
        email: 'casey.ng@acme.com',
        firstName: 'Casey',
        lastName: 'Ng',
        companyName: 'Acme Healthcare',
        requestedRole: 'standard',
        routeTo: 'administrators',
        reviewerLabel: 'Account administrators',
        createdAt: '2026-03-01T12:00:00.000Z',
        status: 'Pending',
      },
    ],
    catalogByCustomer: SEED_CATALOGS,
    pricingPrecedence: ['fixed_sku', 'sku_discount', 'manufacturer_discount', 'catalog_contract_discount'],
    /**
     * DEMO MVP 1.2.1 — approved email domains per account/affiliate.
     * Acme restricts to acme.com; Beta/Gamma empty = any domain accepted.
     */
    approvedEmailDomainsByCustomer: {
      'PRESIDIO-MOCK': ['acme.com'],
      'PRESIDIO-AFF-BETA': [],
      'PRESIDIO-AFF-GAMMA': [],
    },
    // DEMO MVP 1.2.4: Alex's purchase rules → Morgan approves (not parallel DIAS REQ-* story)
    pendingApprovalOrders: [
      {
        id: 'PA-SEED-001',
        requisitionNo: 'PA-1001',
        userLogin: 'alex.rivera@acme.com',
        approverLogin: 'morgan.chen@acme.com',
        reason: 'Order subtotal $72,400 exceeds $25,000',
        createdAt: '2026-03-10T15:00:00.000Z',
        status: 'PENDING',
        totalGross: 72400,
        totalNet: 72400,
        currency: 'USD',
        lineItemCount: 1,
      },
      {
        id: 'PA-SEED-002',
        requisitionNo: 'PA-1002',
        orderNo: 'ORD-PA-1002',
        userLogin: 'alex.rivera@acme.com',
        approverLogin: 'morgan.chen@acme.com',
        reason: 'SKU NV-DGX-H100 requires approval',
        createdAt: '2026-03-05T11:00:00.000Z',
        status: 'APPROVED',
        approvalDate: Date.parse('2026-03-06T14:00:00.000Z'),
        totalGross: 210000,
        totalNet: 210000,
        currency: 'USD',
        lineItemCount: 1,
      },
    ],
  };
}

function readState(): PresidioDiscoveryState {
  if (typeof sessionStorage === 'undefined') {
    return seedState();
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_DISCOVERY_STATE_KEY);
    if (!raw) {
      const seeded = seedState();
      sessionStorage.setItem(PRESIDIO_DISCOVERY_STATE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    const parsed = JSON.parse(raw) as Partial<PresidioDiscoveryState>;
    const seeded = seedState();
    const hiddenById = new Map(
      (parsed.addressMeta || []).map(a => [a.id, !!a.hidden] as const)
    );
    // Refresh address lines from seed; preserve Show/Hide choices from the session.
    const addressMeta = seeded.addressMeta.map(a =>
      enrichAddressMeta({
        ...a,
        hidden: hiddenById.has(a.id) ? !!hiddenById.get(a.id) : a.hidden,
      })
    );
    // Keep demo catalogs current so SKU search matches storefront access after seed updates.
    const pendingApprovalOrders = normalizePendingApprovalOrders(
      parsed.pendingApprovalOrders,
      seeded.pendingApprovalOrders
    );
    return {
      ...seeded,
      ...parsed,
      // Ensure newly seeded demo users appear even when sessionStorage already has an older users list.
      users: mergeDiscoveryUsers(seeded.users, parsed.users),
      catalogByCustomer: SEED_CATALOGS,
      addressMeta,
      pendingApprovalOrders,
      approvedEmailDomainsByCustomer: mergeApprovedEmailDomains(
        seeded.approvedEmailDomainsByCustomer,
        parsed.approvedEmailDomainsByCustomer
      ),
    };
  } catch {
    return seedState();
  }
}

/** Seed owns approver assignment for these logins (no rules ⇒ no approver). */
const SEED_OWNED_APPROVER_LOGINS = new Set(['jamie.stone@acme.com']);

/**
 * Merge seed users into stored session users (add missing seed logins; keep in-session edits).
 * Affiliate assignments always prefer the stored session value so User Management saves stick.
 * (Previously a seed-owned allowlist reset affiliates on every read and looked like saves failed.)
 */
function mergeDiscoveryUsers(
  seeded: PresidioDiscoveryUser[],
  stored: PresidioDiscoveryUser[] | undefined
): PresidioDiscoveryUser[] {
  if (!stored?.length) {
    return seeded;
  }
  const byLogin = new Map(stored.map(u => [u.login.toLowerCase(), u]));
  const merged = seeded.map(seedUser => {
    const existing = byLogin.get(seedUser.login.toLowerCase());
    if (!existing) {
      return seedUser;
    }
    byLogin.delete(seedUser.login.toLowerCase());
    const mergedUser = { ...seedUser, ...existing, login: seedUser.login };
    if (SEED_OWNED_APPROVER_LOGINS.has(seedUser.login.toLowerCase())) {
      mergedUser.approverLogin = seedUser.approverLogin;
    }
    // Preserve stored status (including intentional admin deactivate → Disabled).
    return mergedUser;
  });
  for (const extra of byLogin.values()) {
    merged.push(extra);
  }
  return merged;
}

function normalizePendingApprovalOrders(
  stored: PresidioDiscoveryPendingApprovalOrder[] | undefined,
  seeded: PresidioDiscoveryPendingApprovalOrder[]
): PresidioDiscoveryPendingApprovalOrder[] {
  if (!Array.isArray(stored) || stored.length === 0) {
    return seeded;
  }
  const seedById = new Map(seeded.map(s => [s.id, s]));
  // Migrate legacy { id, userLogin, reason, createdAt } rows; backfill missing reason from seed
  return stored.map(row => {
    const seed = seedById.get(row.id);
    return {
      id: row.id,
      requisitionNo: row.requisitionNo || seed?.requisitionNo || row.id,
      orderNo: row.orderNo ?? seed?.orderNo,
      userLogin: row.userLogin || seed?.userLogin || '',
      approverLogin: row.approverLogin ?? seed?.approverLogin,
      reason: row.reason || seed?.reason || '',
      createdAt: row.createdAt || seed?.createdAt || new Date().toISOString(),
      status: row.status || seed?.status || 'PENDING',
      approvalDate: row.approvalDate ?? seed?.approvalDate,
      approvalComment: row.approvalComment ?? seed?.approvalComment,
      totalGross: row.totalGross ?? seed?.totalGross ?? 0,
      totalNet: row.totalNet ?? row.totalGross ?? seed?.totalNet ?? seed?.totalGross ?? 0,
      currency: row.currency || seed?.currency || 'USD',
      lineItemCount: row.lineItemCount ?? seed?.lineItemCount ?? 0,
      detailPayload: row.detailPayload,
    };
  });
}

function writeState(state: PresidioDiscoveryState): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_DISCOVERY_STATE_KEY, JSON.stringify(state));
}

export function getPresidioDiscoveryState(): PresidioDiscoveryState {
  return readState();
}

export function savePresidioDiscoveryState(state: PresidioDiscoveryState): void {
  writeState(state);
}

export function resetPresidioDiscoveryState(): void {
  writeState(seedState());
}

export function getDiscoveryUser(login?: string | null): PresidioDiscoveryUser | undefined {
  const key = (login || getPresidioMockActiveLogin() || '').toLowerCase();
  return getPresidioDiscoveryState().users.find(u => u.login.toLowerCase() === key);
}

/**
 * MVP 1.2.1 / 1.2.7 — customer administrators may approve pending user access.
 * Standard users (including order-only approvers) never may.
 * CSR is not a customer administrator (handles sales_rep-routed requests separately).
 */
export function isCurrentActorDiscoveryAdministrator(): boolean {
  if (isPresidioMockCsrLogin()) {
    return false;
  }
  return getDiscoveryUser()?.primaryRole === 'administrator';
}

/** Whether the active actor may approve/reject this pending access request. */
export function canCurrentActorMutateAccessRequest(req: PresidioDiscoveryAccessRequest): boolean {
  if (req.routeTo === 'sales_rep') {
    return isPresidioMockCsrLogin();
  }
  return isCurrentActorDiscoveryAdministrator();
}

/** Affiliates the current administrator or CSR may assign (scoped to their own access). */
export function getAssignableAffiliatesForCurrentAdmin(): PresidioDiscoveryAffiliate[] {
  if (isPresidioMockCsrLogin()) {
    return [...PRESIDIO_DISCOVERY_AFFILIATES];
  }
  const admin = getDiscoveryUser();
  // DEMO: when mock active-login is unset, keep assignable affiliates available for admins using the page.
  if (!admin) {
    return [...PRESIDIO_DISCOVERY_AFFILIATES];
  }
  if (admin.primaryRole !== 'administrator') {
    return [];
  }
  return PRESIDIO_DISCOVERY_AFFILIATES.filter(a => admin.affiliateCustomerNos.includes(a.customerNo));
}

export function getAccessRequestsForCurrentActor(): PresidioDiscoveryAccessRequest[] {
  const all = getPresidioDiscoveryState().accessRequests.filter(r => r.status === 'Pending');
  if (isPresidioMockCsrLogin()) {
    return all.filter(r => r.routeTo === 'sales_rep');
  }
  // MVP 1.2.1 — every customer administrator sees admin-routed requests; standard users never do.
  if (!isCurrentActorDiscoveryAdministrator()) {
    return [];
  }
  return all.filter(r => r.routeTo === 'administrators');
}

export function getAffiliatesForLogin(login: string): PresidioDiscoveryAffiliate[] {
  const user = getDiscoveryUser(login);
  if (!user) {
    return [];
  }
  return PRESIDIO_DISCOVERY_AFFILIATES.filter(a => user.affiliateCustomerNos.includes(a.customerNo));
}

/** Assigned affiliates for the active session user (My Account filter dropdowns). */
export function getSessionAssignedAffiliates(): PresidioDiscoveryAffiliate[] {
  return getAffiliatesForLogin(getPresidioMockActiveLogin());
}

/** Overlay for user GET — keep availableSoldTos in sync with discovery affiliate assignments. */
export function applyDiscoveryAvailableSoldTos(body: unknown): unknown {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return body;
  }
  const row = body as Record<string, unknown>;
  const login = String(row.login || getPresidioMockActiveLogin() || '').trim();
  if (!login || !getDiscoveryUser(login)) {
    return body;
  }
  const affiliates = getAffiliatesForLogin(login);
  return {
    ...row,
    availableSoldTos: affiliates.map(a => ({
      customerNo: a.customerNo,
      companyName: a.companyName,
    })),
  };
}

export function upsertDiscoveryUser(user: PresidioDiscoveryUser): void {
  const state = getPresidioDiscoveryState();
  const idx = state.users.findIndex(u => u.login.toLowerCase() === user.login.toLowerCase());
  if (idx >= 0) {
    state.users[idx] = user;
  } else {
    state.users.push(user);
  }
  writeState(state);
}

/** Merge stored per-customer domain lists over seed defaults (empty array is intentional). */
function mergeApprovedEmailDomains(
  seeded: Record<string, string[]>,
  stored: Record<string, string[]> | undefined
): Record<string, string[]> {
  const next: Record<string, string[]> = { ...seeded };
  if (!stored) {
    return next;
  }
  for (const [customerNo, domains] of Object.entries(stored)) {
    if (Array.isArray(domains)) {
      next[customerNo] = domains.map(d => String(d || '').trim().toLowerCase().replace(/^@+/, '')).filter(Boolean);
    }
  }
  return next;
}

export function getApprovedEmailDomainsByCustomer(): Record<string, string[]> {
  return { ...(getPresidioDiscoveryState().approvedEmailDomainsByCustomer || {}) };
}

const DOMAIN_CHANGE_NOTICES_KEY = 'presidio.discovery.domainChangeNotices';

/** DEMO-ONLY: outbound admin notification stub for approved-domain changes (not live SMTP). */
export interface PresidioApprovedEmailDomainChangeNotice {
  action: 'added' | 'removed';
  domain: string;
  customerNo: string;
  recipientEmails: string[];
  at: string;
}

/** Active administrators assigned to the given account (for domain-change notices). */
export function getActiveAccountAdministrators(customerNo: string): PresidioDiscoveryUser[] {
  const key = String(customerNo || '').trim();
  if (!key) {
    return [];
  }
  return getPresidioDiscoveryState().users.filter(user => {
    if (String(user.primaryRole || '').toLowerCase() !== 'administrator') {
      return false;
    }
    const status = String(user.status || 'Active').toLowerCase();
    if (status === 'disabled' || status === 'pending') {
      return false;
    }
    return (user.affiliateCustomerNos || []).includes(key);
  });
}

export function listApprovedEmailDomainChangeNotices(): PresidioApprovedEmailDomainChangeNotice[] {
  if (typeof sessionStorage === 'undefined') {
    return [];
  }
  try {
    const raw = sessionStorage.getItem(DOMAIN_CHANGE_NOTICES_KEY);
    return raw ? (JSON.parse(raw) as PresidioApprovedEmailDomainChangeNotice[]) : [];
  } catch {
    return [];
  }
}

function appendApprovedEmailDomainChangeNotices(notices: PresidioApprovedEmailDomainChangeNotice[]): void {
  if (!notices.length || typeof sessionStorage === 'undefined') {
    return;
  }
  const all = [...notices, ...listApprovedEmailDomainChangeNotices()].slice(0, 50);
  sessionStorage.setItem(DOMAIN_CHANGE_NOTICES_KEY, JSON.stringify(all));
}

/**
 * Persist approved domains and DEMO-notify all account administrators for each
 * added or removed domain (sessionStorage stub; production would send email).
 */
export function setApprovedEmailDomainsForCustomer(
  customerNo: string,
  domains: string[]
): PresidioApprovedEmailDomainChangeNotice[] {
  const state = getPresidioDiscoveryState();
  const key = String(customerNo || '').trim();
  const previous = new Set((state.approvedEmailDomainsByCustomer || {})[key] || []);
  const normalized = (domains || [])
    .map(d => String(d || '').trim().toLowerCase().replace(/^@+/, '').replace(/\.+$/, ''))
    .filter(d => !!d && d.includes('.') && !d.includes('@'));
  const next = Array.from(new Set(normalized));
  const nextSet = new Set(next);

  state.approvedEmailDomainsByCustomer = {
    ...(state.approvedEmailDomainsByCustomer || {}),
    [key]: next,
  };
  writeState(state);

  const admins = getActiveAccountAdministrators(key);
  const recipientEmails = Array.from(
    new Set(admins.map(admin => String(admin.email || admin.login || '').trim().toLowerCase()).filter(Boolean))
  );
  const at = new Date().toISOString();
  const notices: PresidioApprovedEmailDomainChangeNotice[] = [];
  for (const domain of next) {
    if (!previous.has(domain)) {
      notices.push({ action: 'added', domain, customerNo: key, recipientEmails, at });
    }
  }
  for (const domain of previous) {
    if (!nextSet.has(domain)) {
      notices.push({ action: 'removed', domain, customerNo: key, recipientEmails, at });
    }
  }
  appendApprovedEmailDomainChangeNotices(notices);
  return notices;
}

export function setAddressHidden(addressMetaId: string, hidden: boolean): void {
  const state = getPresidioDiscoveryState();
  const row = state.addressMeta.find(a => a.id === addressMetaId);
  if (row) {
    row.hidden = hidden;
    writeState(state);
  }
}

export function getVisibleAddressIds(customerNo: string, kind?: 'billing' | 'shipping'): string[] {
  return getPresidioDiscoveryState()
    .addressMeta.filter(
      a => a.customerNo === customerNo && !a.hidden && (!kind || a.kind === kind)
    )
    .sort((a, b) => Number(!!b.recentlyUsed) - Number(!!a.recentlyUsed))
    .map(a => a.id.split(':')[1] || a.id);
}

export function isAddressHidden(customerNo: string, addressId: string): boolean {
  const id = addressId.includes(':') ? addressId : `${customerNo}:${addressId}`;
  return !!getPresidioDiscoveryState().addressMeta.find(a => a.id === id)?.hidden;
}

export function getCatalogForActiveCustomer(): PresidioDiscoveryCatalogConfig | undefined {
  const customerNo = getPresidioMockActiveCustomerNo() || 'PRESIDIO-MOCK';
  return getPresidioDiscoveryState().catalogByCustomer[customerNo];
}

export function isSkuAllowedForActiveCustomer(sku: string): boolean {
  const catalog = getCatalogForActiveCustomer();
  if (!catalog) {
    return true;
  }
  return catalog.allowedSkus.includes(sku);
}

export function getNegotiatedPrice(sku: string): number | undefined {
  return getCatalogForActiveCustomer()?.negotiatedPrices[sku];
}

export function evaluatePurchaseRules(input: {
  userLogin: string;
  subtotal: number;
  lines: { sku: string; unitPrice: number }[];
}): { triggered: boolean; reason: string } {
  const all = evaluateAllPurchaseRules(input);
  return { triggered: all.triggered, reason: all.reasons[0] || '' };
}

/** All exceeded purchase rules for checkout messaging (MVP 1.2.4). */
export function evaluateAllPurchaseRules(input: {
  userLogin: string;
  subtotal: number;
  lines: { sku: string; unitPrice: number }[];
}): { triggered: boolean; reasons: string[] } {
  const rules = getPresidioDiscoveryState().purchaseRules.filter(
    r => r.userLogin.toLowerCase() === input.userLogin.toLowerCase()
  );
  const reasons: string[] = [];
  for (const rule of rules) {
    if (rule.type === 'OrderSubtotal' && rule.threshold != null && input.subtotal > rule.threshold) {
      reasons.push(
        `Order subtotal $${input.subtotal.toLocaleString()} exceeds $${rule.threshold.toLocaleString()}`
      );
    }
    if (rule.type === 'LinePrice' && rule.threshold != null) {
      const hit = input.lines.find(l => l.unitPrice > rule.threshold!);
      if (hit) {
        reasons.push(
          `Line ${hit.sku} price $${hit.unitPrice.toLocaleString()} exceeds $${rule.threshold.toLocaleString()}`
        );
      }
    }
    if (rule.type === 'Sku' && rule.sku) {
      if (input.lines.some(l => l.sku === rule.sku)) {
        reasons.push(`SKU ${rule.sku} requires approval`);
      }
    }
  }
  return { triggered: reasons.length > 0, reasons };
}

/** Checkout banner payload: exceeded rules + assigned approver display name. */
export function getPurchaseRuleCheckoutNotice(input: {
  userLogin: string;
  subtotal: number;
  lines: { sku: string; unitPrice: number }[];
  /**
   * FR10.10.2 / FR13.7.6 — quote carts ignore assigned purchase rules
   * (quote Approve is separate from checkout purchase-rule approval).
   */
  hasQuoteLines?: boolean;
}): { triggered: boolean; reasons: string[]; approverDisplayName?: string } {
  if (input.hasQuoteLines) {
    return { triggered: false, reasons: [] };
  }
  const evaluation = evaluateAllPurchaseRules(input);
  if (!evaluation.triggered) {
    return { triggered: false, reasons: [] };
  }
  const buyer = getDiscoveryUser(input.userLogin);
  const approver = buyer?.approverLogin ? getDiscoveryUser(buyer.approverLogin) : undefined;
  const approverDisplayName = approver
    ? `${approver.firstName || ''} ${approver.lastName || ''}`.trim() || approver.login
    : undefined;
  return {
    triggered: true,
    reasons: evaluation.reasons,
    approverDisplayName,
  };
}

export function recordPendingApprovalOrder(
  userLogin: string,
  reason: string,
  meta?: {
    orderId?: string;
    /** @deprecated Prefer `requisitionNo` — documentNo was conflated with request #. */
    documentNo?: string;
    /** Shopper-facing request number (shown on confirmation instead of order #). */
    requisitionNo?: string;
    totalGross?: number;
    totalNet?: number;
    currency?: string;
    lineItemCount?: number;
    detailPayload?: unknown;
  }
): string {
  const state = getPresidioDiscoveryState();
  const buyer = state.users.find(u => u.login.toLowerCase() === userLogin.toLowerCase());
  const id = meta?.orderId || `PA-${Date.now()}`;
  const requisitionNo = meta?.requisitionNo || meta?.documentNo || id;
  const entry: PresidioDiscoveryPendingApprovalOrder = {
    id,
    requisitionNo,
    orderNo: undefined,
    userLogin,
    approverLogin: buyer?.approverLogin,
    reason,
    createdAt: new Date().toISOString(),
    status: 'PENDING',
    totalGross: meta?.totalGross ?? 0,
    totalNet: meta?.totalNet ?? meta?.totalGross ?? 0,
    currency: meta?.currency || 'USD',
    lineItemCount: meta?.lineItemCount ?? 0,
    detailPayload: meta?.detailPayload,
  };
  state.pendingApprovalOrders = [entry, ...state.pendingApprovalOrders.filter(o => o.id !== id)];
  writeState(state);
  return id;
}

export function listPendingApprovalOrdersForView(
  view: 'buyer' | 'approver',
  status?: PresidioDiscoveryPendingApprovalStatus
): PresidioDiscoveryPendingApprovalOrder[] {
  const login = (getPresidioMockActiveLogin() || '').toLowerCase();
  const state = getPresidioDiscoveryState();
  return state.pendingApprovalOrders.filter(o => {
    if (status && o.status !== status) {
      return false;
    }
    if (view === 'buyer') {
      return o.userLogin.toLowerCase() === login;
    }
    // Approver view: only requests assigned to this approver (MVP 1.2.6 approval contact)
    return o.approverLogin?.toLowerCase() === login;
  });
}

/**
 * Pending purchase-order approvals + pending user-access requests + Submitted quotes
 * for the Approvals nav badge (FR2.2.9 / FR18.6.5).
 */
export function getPendingApprovalsCountForCurrentActor(): number {
  const orderCount = listPendingApprovalOrdersForView('approver', 'PENDING').length;
  const accessCount = getAccessRequestsForCurrentActor().length;
  const quoteCount = countPresidioQuotesPendingApproval();
  return orderCount + accessCount + quoteCount;
}

export function getPendingApprovalOrder(id: string): PresidioDiscoveryPendingApprovalOrder | undefined {
  const key = decodeURIComponent(id || '').toLowerCase();
  return getPresidioDiscoveryState().pendingApprovalOrders.find(
    o => o.id.toLowerCase() === key || o.requisitionNo.toLowerCase() === key
  );
}

export function updatePendingApprovalOrderStatus(
  id: string,
  status: PresidioDiscoveryPendingApprovalStatus,
  approvalComment?: string
): PresidioDiscoveryPendingApprovalOrder | undefined {
  const state = getPresidioDiscoveryState();
  const order = state.pendingApprovalOrders.find(
    o => o.id === id || o.requisitionNo === id || o.id.toLowerCase() === id.toLowerCase()
  );
  if (!order) {
    return undefined;
  }
  order.status = status;
  order.approvalComment = approvalComment;
  if (status === 'APPROVED' || status === 'REJECTED') {
    order.approvalDate = Date.now();
    if (status === 'APPROVED' && !order.orderNo) {
      order.orderNo = `ORD-${order.requisitionNo}`;
    }
  }
  writeState(state);
  return order;
}
export function addAccessRequest(req: Omit<PresidioDiscoveryAccessRequest, 'id' | 'createdAt' | 'status'>): PresidioDiscoveryAccessRequest {
  const state = getPresidioDiscoveryState();
  const full: PresidioDiscoveryAccessRequest = {
    ...req,
    id: `AR-${Date.now()}`,
    createdAt: new Date().toISOString(),
    status: 'Pending',
  };
  state.accessRequests.unshift(full);

  // Ensure the requester exists as Pending so login can block until approved.
  const email = (req.email || '').trim().toLowerCase();
  if (email) {
    const existing = state.users.find(u => u.login.toLowerCase() === email || u.email.toLowerCase() === email);
    if (existing) {
      if (existing.status !== 'Active') {
        existing.status = 'Pending';
      }
    } else {
      state.users.push({
        login: email,
        firstName: req.firstName || email.split('@')[0] || 'New',
        lastName: req.lastName || 'User',
        email,
        primaryRole: req.requestedRole,
        canApprove: false,
        status: 'Pending',
        // Every user has at least one assigned account; approve may expand the list.
        affiliateCustomerNos: [getPresidioMockActiveCustomerNo() || 'PRESIDIO-MOCK'],
      });
    }
  }

  writeState(state);
  return full;
}

/** DEMO: block password login until an administrator approves the account. */
export function getDiscoveryLoginBlockReason(
  login: string | null | undefined
): 'pending' | 'disabled' | undefined {
  const key = (login || '').trim().toLowerCase();
  if (!key) {
    return undefined;
  }
  // Match login or email — request-access rows may only populate email.
  const user = getPresidioDiscoveryState().users.find(
    u => u.login.toLowerCase() === key || (u.email || '').toLowerCase() === key
  );
  if (user?.status === 'Pending') {
    return 'pending';
  }
  if (user?.status === 'Disabled') {
    return 'disabled';
  }
  const pendingRequest = getPresidioDiscoveryState().accessRequests.find(
    r => r.status === 'Pending' && r.email.toLowerCase() === key
  );
  if (pendingRequest) {
    return 'pending';
  }
  return undefined;
}

export function approveAccessRequest(
  id: string,
  overlay?: Partial<
    Pick<
      PresidioDiscoveryUser,
      | 'affiliateCustomerNos'
      | 'primaryRole'
      | 'canApprove'
      | 'firstName'
      | 'lastName'
      | 'approverLogin'
      | 'department'
      | 'title'
      | 'phone'
    >
  >
): void {
  const state = getPresidioDiscoveryState();
  const req = state.accessRequests.find(r => r.id === id);
  if (!req || !canCurrentActorMutateAccessRequest(req)) {
    return;
  }
  const pendingUser = state.users.find(u => u.email.toLowerCase() === req.email.toLowerCase());
  const affiliates =
    overlay?.affiliateCustomerNos && overlay.affiliateCustomerNos.length
      ? overlay.affiliateCustomerNos
      : pendingUser?.affiliateCustomerNos?.length
        ? pendingUser.affiliateCustomerNos
        : [getPresidioMockActiveCustomerNo() || 'PRESIDIO-MOCK'];
  const department = overlay?.department !== undefined ? overlay.department : req.department;
  const title = overlay?.title !== undefined ? overlay.title : req.title;
  const phone = overlay?.phone !== undefined ? overlay.phone : req.phone;
  if (pendingUser) {
    pendingUser.status = 'Active';
    pendingUser.primaryRole = overlay?.primaryRole || req.requestedRole;
    pendingUser.canApprove = overlay?.canApprove ?? pendingUser.canApprove;
    pendingUser.affiliateCustomerNos = affiliates;
    if (overlay?.firstName) {
      pendingUser.firstName = overlay.firstName;
    }
    if (overlay?.lastName) {
      pendingUser.lastName = overlay.lastName;
    }
    if (overlay?.approverLogin !== undefined) {
      pendingUser.approverLogin = overlay.approverLogin;
    }
    pendingUser.department = department;
    pendingUser.title = title;
    pendingUser.phone = phone;
  } else {
    state.users.push({
      login: req.email,
      firstName: overlay?.firstName || req.firstName || req.email.split('@')[0] || 'New',
      lastName: overlay?.lastName || req.lastName || 'User',
      email: req.email,
      primaryRole: overlay?.primaryRole || req.requestedRole,
      canApprove: overlay?.canApprove ?? false,
      status: 'Active',
      affiliateCustomerNos: affiliates,
      approverLogin: overlay?.approverLogin,
      department,
      title,
      phone,
    });
  }
  state.accessRequests = state.accessRequests.filter(r => r.id !== id);
  writeState(state);
}

const ACCESS_REJECTION_KEY = 'presidio-mock-access-rejections';

/** DEMO: decline access — optional comments are stored as if emailed to the requester (no PII in analytics). */
export function rejectAccessRequest(id: string, comments?: string): void {
  const state = getPresidioDiscoveryState();
  const req = state.accessRequests.find(r => r.id === id);
  if (!req || !canCurrentActorMutateAccessRequest(req)) {
    return;
  }
  const pendingUser = state.users.find(u => u.email.toLowerCase() === req.email.toLowerCase());
  if (pendingUser && pendingUser.status === 'Pending') {
    pendingUser.status = 'Disabled';
  }
  state.accessRequests = state.accessRequests.filter(r => r.id !== id);
  writeState(state);
  if (typeof sessionStorage !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(ACCESS_REJECTION_KEY);
      const list = raw ? (JSON.parse(raw) as unknown[]) : [];
      list.unshift({
        id,
        email: req.email,
        hasComments: !!String(comments || '').trim(),
        rejectedAt: new Date().toISOString(),
      });
      sessionStorage.setItem(ACCESS_REJECTION_KEY, JSON.stringify(list.slice(0, 20)));
    } catch {
      /* ignore demo storage errors */
    }
  }
}

export function mapRoleIdsToDiscovery(roleIDs: string[] | undefined): {
  primaryRole: PresidioDiscoveryPrimaryRole;
  canApprove: boolean;
} {
  const ids = roleIDs || [];
  const canApprove = ids.includes('APP_B2B_APPROVER');
  const primaryRole =
    ids.includes('APP_B2B_ACCOUNT_OWNER') || ids.includes('APP_B2B_ADMIN')
      ? 'administrator'
      : 'standard';
  return { primaryRole, canApprove };
}

export function discoveryRoleToRoleIds(
  primaryRole: PresidioDiscoveryPrimaryRole,
  canApprove: boolean
): string[] {
  const ids: string[] =
    primaryRole === 'administrator'
      ? ['APP_B2B_BUYER', 'APP_B2B_ACCOUNT_OWNER']
      : ['APP_B2B_BUYER'];
  if (canApprove) {
    ids.push('APP_B2B_APPROVER');
  }
  return ids;
}
